import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Settings,
  LogOut,
  Trophy,
  Gift,
  Calendar,
  Heart,
  HelpCircle,
  ChevronDown,
  Bell,
  Star,
  Scissors,
  Users,
  CreditCard,
  FileText,
  Shield,
  Sparkles,
  Code,
  Edit2
} from "lucide-react";
import { useAuth } from "@/App";

const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

const ProfileDropdown = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    return parts.length > 1 
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const getRoleBadge = () => {
    switch (user?.role) {
      case "owner":
        return { label: "Owner", color: "bg-purple-500" };
      case "stylist":
        return { label: "Stylist", color: "bg-amber-500" };
      default:
        return { label: "Client", color: "bg-blue-500" };
    }
  };

  const roleBadge = getRoleBadge();

  // Menu items based on user role
  const getMenuItems = () => {
    const commonItems = [
      { type: "divider", label: "Account" },
      { 
        icon: User, 
        label: "My Profile", 
        href: user?.role === "client" ? "/dashboard" : "/stylist",
        description: "View and edit your profile"
      },
      { 
        icon: Settings, 
        label: "Settings", 
        href: user?.role === "client" ? "/dashboard" : "/stylist",
        description: "Account preferences"
      },
      { 
        icon: Bell, 
        label: "Notifications", 
        href: user?.role === "client" ? "/dashboard" : "/stylist",
        badge: "3",
        description: "Alerts & updates"
      },
    ];

    const clientItems = [
      { type: "divider", label: "Quick Actions" },
      { 
        icon: Calendar, 
        label: "My Appointments", 
        href: "/dashboard",
        description: "View upcoming bookings"
      },
      { 
        icon: Edit2, 
        label: "Edit/Cancel Booking", 
        href: "/dashboard",
        description: "Modify your appointments"
      },
      { 
        icon: Heart, 
        label: "Favorites", 
        href: "/dashboard",
        description: "Saved stylists"
      },
      { 
        icon: Gift, 
        label: "Loyalty Points", 
        href: "/dashboard",
        badge: user?.loyalty_points || "0",
        description: "Rewards balance"
      },
      { 
        icon: Trophy, 
        label: "Referral Program", 
        href: "/dashboard",
        badge: "NEW",
        badgeColor: "bg-amber-500",
        description: "Earn rewards"
      },
    ];

    const stylistItems = [
      { type: "divider", label: "Business" },
      { 
        icon: Calendar, 
        label: "Dashboard", 
        href: "/stylist",
        description: "Your business hub"
      },
      { 
        icon: Users, 
        label: "Client Notes", 
        href: "/stylist",
        description: "Manage clients"
      },
      { 
        icon: CreditCard, 
        label: "Earnings", 
        href: "/stylist",
        description: "Revenue & payments"
      },
      { 
        icon: Trophy, 
        label: "Referral Program", 
        href: "/stylist",
        badge: "NEW",
        badgeColor: "bg-amber-500",
        description: "Earn rewards"
      },
      { 
        icon: Star, 
        label: "Upgrade Plan", 
        href: "/membership",
        badge: "PRO",
        badgeColor: "bg-purple-500",
        description: "Unlock features"
      },
    ];

    const ownerItems = [
      { type: "divider", label: "Business" },
      { 
        icon: Calendar, 
        label: "Dashboard", 
        href: "/stylist",
        description: "Your business hub"
      },
      { 
        icon: Users, 
        label: "Staff Management", 
        href: "/stylist",
        description: "Manage your team"
      },
      { 
        icon: CreditCard, 
        label: "Accounting", 
        href: "/stylist",
        badge: "ELITE",
        badgeColor: "bg-purple-500",
        description: "Finances & reports"
      },
      { 
        icon: Trophy, 
        label: "Referral Program", 
        href: "/stylist",
        badge: "NEW",
        badgeColor: "bg-amber-500",
        description: "Earn rewards"
      },
      { 
        icon: Sparkles, 
        label: "Beta Analytics", 
        href: "/stylist",
        description: "Usage insights"
      },
    ];

    const supportItems = [
      { type: "divider", label: "Support" },
      { 
        icon: HelpCircle, 
        label: "Help Center", 
        href: "/faq",
        description: "Get assistance"
      },
      { 
        icon: FileText, 
        label: "Documentation", 
        href: "/referral-program",
        description: "How things work"
      },
      { 
        icon: Shield, 
        label: "HairChair Soldiers", 
        href: "/soldiers",
        badge: "EARN",
        badgeColor: "bg-amber-500",
        description: "Affiliate program"
      },
    ];

    // Super Admin menu items (using component-level isSuperAdmin)
    const adminItems = isSuperAdmin ? [
      { type: "divider", label: "Administration" },
      { 
        icon: Shield, 
        label: "Super Admin Panel", 
        href: "/admin",
        badge: "ADMIN",
        badgeColor: "bg-red-500",
        description: "Platform control center"
      },
      { 
        icon: Code, 
        label: "Developer Portal", 
        href: "/developer",
        badge: "DEV",
        badgeColor: "bg-cyan-500",
        description: "Developer docs & tools"
      },
    ] : [];

    let roleSpecificItems;
    switch (user?.role) {
      case "owner":
        roleSpecificItems = ownerItems;
        break;
      case "stylist":
        roleSpecificItems = stylistItems;
        break;
      default:
        roleSpecificItems = clientItems;
    }

    return [...commonItems, ...roleSpecificItems, ...supportItems, ...adminItems];
  };

  const menuItems = getMenuItems();

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-xl hover:bg-stone-800 transition-colors"
        data-testid="profile-dropdown-trigger"
      >
        {/* Avatar */}
        <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-sm">
          {getInitials(user?.name)}
        </div>
        
        {/* Name & Role (hidden on mobile) */}
        <div className="hidden md:block text-left">
          <p className="text-white text-sm font-medium leading-tight">
            {user?.name?.split(" ")[0] || "User"}
          </p>
          <p className="text-stone-500 text-xs">{roleBadge.label}</p>
        </div>
        
        <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-stone-900 border border-stone-700 rounded-xl shadow-xl overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User Header */}
          <div className="p-4 bg-gradient-to-r from-stone-800 to-stone-900 border-b border-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold">
                {getInitials(user?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name || "User"}</p>
                <p className="text-stone-400 text-sm truncate">{user?.email}</p>
              </div>
              <Badge className={`${roleBadge.color} text-white text-xs`}>
                {roleBadge.label}
              </Badge>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2 max-h-[400px] overflow-y-auto">
            {menuItems.map((item, idx) => {
              if (item.type === "divider") {
                return (
                  <div key={idx} className="px-4 py-2 mt-1">
                    <p className="text-stone-500 text-xs font-medium uppercase tracking-wider">
                      {item.label}
                    </p>
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-stone-800 transition-colors group"
                >
                  <div className="w-8 h-8 bg-stone-800 group-hover:bg-stone-700 rounded-lg flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-stone-400 group-hover:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm">{item.label}</span>
                      {item.badge && (
                        <Badge className={`${item.badgeColor || 'bg-stone-700'} text-white text-xs px-1.5 py-0`}>
                          {item.badge}
                        </Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-stone-500 text-xs truncate">{item.description}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Logout Button */}
          <div className="p-2 border-t border-stone-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-500/10 rounded-lg transition-colors group"
            >
              <div className="w-8 h-8 bg-stone-800 group-hover:bg-red-500/20 rounded-lg flex items-center justify-center">
                <LogOut className="w-4 h-4 text-stone-400 group-hover:text-red-400" />
              </div>
              <span className="text-stone-300 group-hover:text-red-400 text-sm">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
